<template>
  <div class="product-line-chart">
    <div v-if="loading" class="chart-loading">
      <el-icon class="is-loading"><i class="el-icon-loading"></i></el-icon>
      <span>数据加载中...</span>
    </div>
    <div v-else-if="!hasData" class="chart-empty">
      <i class="el-icon-s-grid"></i>
      <span>暂无数据</span>
    </div>
    <div v-else ref="chart" class="chart-container"></div>
  </div>
</template>

<script>
import echarts from 'echarts'

export default {
  name: 'product-line-chart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      chart: null,
      resizeHandler: null
    }
  },

  computed: {
    hasData() {
      return this.data && this.data.length > 0
    },

    chartData() {
      if (!this.hasData) return { names: [], times: [], fees: [] }

      const sortedData = [...this.data]
        .filter(item => (item.totalTimes || 0) > 0)
        .sort((a, b) => (b.totalTimes || 0) - (a.totalTimes || 0))

      return {
        names: sortedData.map(item => item.productLineName || '未知产品线'),
        times: sortedData.map(item => item.totalTimes || 0),
        fees: sortedData.map(item => item.totalFee || 0)
      }
    }
  },

  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          if (this.hasData && !this.chart) {
            this.initChart()
          } else if (this.chart) {
            this.renderChart()
          }
        })
      },
      deep: true
    },

    loading(newVal) {
      if (!newVal && this.hasData) {
        this.$nextTick(() => {
          if (!this.chart) {
            this.initChart()
          } else {
            this.renderChart()
          }
        })
      }
    }
  },

  mounted() {
    this.initChart()
    this.bindEvents()
  },

  beforeDestroy() {
    this.unbindEvents()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },

  methods: {
    /**
     * 初始化图表
     */
    initChart() {
      if (!this.$refs.chart) return

      this.chart = echarts.init(this.$refs.chart)
      this.renderChart()
    },

    /**
     * 渲染图表
     */
    renderChart() {
      console.log('产品线图表渲染开始:', {
        hasChart: !!this.chart,
        hasData: this.hasData,
        dataLength: this.data ? this.data.length : 0,
        chartData: this.chartData
      })

      if (!this.chart || !this.hasData) {
        console.log('产品线图表渲染终止:', { hasChart: !!this.chart, hasData: this.hasData })
        return
      }

      const { names, times, fees } = this.chartData

      const option = {
        title: {
          text: '产品线使用分布',
          left: 'center',
          top: 20,
          textStyle: {
            fontSize: 16,
            color: '#333'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          formatter: (params) => {
            const dataIndex = params[0].dataIndex
            const productLineName = names[dataIndex]
            const timesValue = times[dataIndex]
            const feeValue = fees[dataIndex]

            let result = `<div><strong>${productLineName}</strong><br/>`

            params.forEach((param, index) => {
              // 优化颜色：使用更深的颜色以便在浅色背景下清晰可见
              const color = index === 0 ? '#1a3a8a' : '#2d5f2e'
              const value = index === 0
                ? this.formatNumber(timesValue)
                : '¥' + this.formatMoney(feeValue)
              result += `${param.seriesName}: <span style="color: ${color}; font-weight: 600;">${value}</span><br/>`
            })

            result += '</div>'
            return result
          }
        },
        legend: {
          data: ['调用次数', '费用金额'],
          top: 50,
          left: 'center'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '20%',
          containLabel: true
        },
        xAxis: [
          {
            type: 'category',
            data: names,
            axisPointer: {
              type: 'shadow'
            },
            axisLabel: {
              rotate: names.length > 5 ? 45 : 0,
              interval: 0,
              fontSize: 11,
              color: '#666',
              formatter: (value) => {
                return value.length > 8 ? value.substring(0, 8) + '...' : value
              }
            },
            axisTick: {
              alignWithLabel: true
            },
            axisLine: {
              lineStyle: {
                color: '#E4E7ED'
              }
            }
          }
        ],
        yAxis: [
          {
            type: 'value',
            name: '调用次数',
            position: 'left',
            nameTextStyle: {
              color: '#5470C6',
              fontSize: 12
            },
            axisLabel: {
              color: '#666',
              formatter: (value) => {
                if (value >= 1000000) {
                  return (value / 1000000).toFixed(1) + 'M'
                } else if (value >= 1000) {
                  return (value / 1000).toFixed(1) + 'K'
                }
                return value.toString()
              }
            },
            axisLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            splitLine: {
              lineStyle: {
                color: '#E4E7ED',
                type: 'dashed'
              }
            }
          },
          {
            type: 'value',
            name: '费用金额 (¥)',
            position: 'right',
            nameTextStyle: {
              color: '#91CC75',
              fontSize: 12
            },
            axisLabel: {
              color: '#666',
              formatter: (value) => {
                if (value >= 10000) {
                  return (value / 10000).toFixed(1) + 'W'
                } else if (value >= 1000) {
                  return (value / 1000).toFixed(1) + 'K'
                }
                return value.toFixed(0)
              }
            },
            axisLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            splitLine: {
              show: false
            }
          }
        ],
        series: [
          {
            name: '调用次数',
            type: 'bar',
            yAxisIndex: 0,
            data: times,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#667eea' },
                { offset: 1, color: '#764ba2' }
              ]),
              borderRadius: [6, 6, 0, 0],
              shadowColor: 'rgba(102, 126, 234, 0.3)',
              shadowBlur: 10
            },
            emphasis: {
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#7e8ff0' },
                  { offset: 1, color: '#8d5bb2' }
                ]),
                shadowColor: 'rgba(102, 126, 234, 0.5)',
                shadowBlur: 20
              }
            },
            animationDelay: function (idx) {
              return idx * 100
            }
          },
          {
            name: '费用金额',
            type: 'line',
            yAxisIndex: 1,
            data: fees,
            lineStyle: {
              width: 4,
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: '#5daf7f' },
                { offset: 1, color: '#4ab8a8' }
              ]),
              shadowColor: 'rgba(93, 175, 127, 0.3)',
              shadowBlur: 10,
              shadowOffsetY: 5
            },
            itemStyle: {
              color: '#5daf7f',
              borderWidth: 3,
              borderColor: '#fff',
              shadowColor: 'rgba(93, 175, 127, 0.4)',
              shadowBlur: 8
            },
            symbol: 'circle',
            symbolSize: 10,
            emphasis: {
              itemStyle: {
                color: '#6fc593',
                borderWidth: 4,
                shadowColor: 'rgba(93, 175, 127, 0.6)',
                shadowBlur: 15
              },
              scale: true,
              scaleSize: 15
            },
            animationDelay: function (idx) {
              return idx * 100 + 500
            }
          }
        ],
        animationEasing: 'elasticOut',
        animationDelayUpdate: function (idx) {
          return idx * 50
        }
      }

      this.chart.setOption(option, true)
    },

    /**
     * 绑定事件
     */
    bindEvents() {
      this.resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    /**
     * 解绑事件
     */
    unbindEvents() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    },

    /**
     * 格式化数字
     */
    formatNumber(num) {
      if (!num) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    /**
     * 格式化金额
     */
    formatMoney(amount) {
      if (!amount) return '0.00'
      return Number(amount).toFixed(2)
    }
  }
}
</script>

<style scoped>
.product-line-chart {
  height: 100%;
  position: relative;
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  border-radius: 12px;
  padding: 8px;
}

.chart-container {
  width: 100%;
  height: 100%;
  min-height: 380px;
  animation: chartZoomIn 0.8s ease-out;
}

@keyframes chartZoomIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.chart-loading,
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 320px;
  color: #909399;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, rgba(250, 250, 250, 0.9) 100%);
  border-radius: 8px;
}

.chart-loading {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.chart-loading .el-icon {
  font-size: 32px;
  margin-bottom: 12px;
  color: #667eea;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.chart-empty i {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
  color: #667eea;
  animation: swing 2s ease-in-out infinite;
}

@keyframes swing {
  0%, 100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(10deg);
  }
  75% {
    transform: rotate(-10deg);
  }
}

.chart-empty span {
  font-size: 15px;
  color: #909399;
  font-weight: 500;
}
</style>



// WEBPACK FOOTER //
// src/custom/components/stats/message-stats/components/product-line-chart.vue